<script setup lang="ts">
import { LucideBookmark } from 'lucide-vue-next'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { FeaturePanelSidebar } from '~/components/feature/panel-sidebar'
import { EntityNewsCard, type TNewsCard } from '~/components/entity/news'
import { EntityNewsSkeletonCard } from '~/components/entity/news/skeleton'
import { FeaturePagination } from '~/components/feature/pagination'
import { Alert, AlertDescription } from '~/components/ui/alert'
import type { IPaginationResponseMeta, IPaginationResponseLinks } from '~/components/feature/pagination'

const { t } = useI18n()
const breadcrumbs = [
  { title: t('page.panel.breadcrumb'), url: '/panel' },
  { title: t('page.panel.favorites.breadcrumb'), url: '/panel/favorites' }
]

const route = useRoute()
const { getPathWithLocale } = useLocalizedPath()
const { isLoggedIn, favoriteIds } = useUser()
const { getFavoriteNews, isLoading, error } = useFavorites()

if (!isLoggedIn.value) {
  await navigateTo(getPathWithLocale('/login'))
}

const page = ref<number>(Number(route.query.page || 1))
const favoriteNews = ref<TNewsCard[]>([])
const meta = ref<IPaginationResponseMeta | null>(null)
const links = ref<IPaginationResponseLinks | null>(null)

const loadFavoriteNews = async (pageNumber = page.value) => {
  try {
    const result = await getFavoriteNews(pageNumber)

    favoriteNews.value = result.data
    meta.value = result.meta
    links.value = result.links
  } catch (err) {
    console.error('Failed to load favorite news:', err)
  }
}

const fetchFavorites = (pageNumber: number) => {
  page.value = pageNumber
  loadFavoriteNews(pageNumber)
}

onMounted(() => {
  loadFavoriteNews()
})

watch(favoriteIds, (newFavoriteIds) => {
  if (favoriteNews.value.length > 0) {
    favoriteNews.value = favoriteNews.value.filter((news) => newFavoriteIds.includes(news.id))
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <div class="space-y-5">
          <ContentBlock variant="white">
            <Heading tag="h1">
              {{ $t('page.panel.favorites.heading') }}
            </Heading>
            <p class="text-gray-700">{{ $t('page.panel.favorites.subheading') }}</p>
          </ContentBlock>

          <Alert v-if="error" variant="red" class="mb-6">
            <AlertDescription>{{ error }}</AlertDescription>
          </Alert>

          <div v-if="isLoading">
            <EntityNewsSkeletonCard />
          </div>

          <template v-else>
            <Alert v-if="favoriteNews.length === 0" variant="blue">
              <LucideBookmark class="mb-8 h-4 w-4" />
              <AlertDescription>{{ $t('page.panel.favorites.empty') }}</AlertDescription>
            </Alert>

            <div v-else class="mb-5">
              <div class="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3">
                <EntityNewsCard v-for="news in favoriteNews" :key="news.id" v-bind="news" />
              </div>
            </div>
            <div v-if="links && meta" class="flex justify-end">
              <FeaturePagination :meta="meta" :links="links" @change="fetchFavorites" />
            </div>
          </template>
        </div>
        <template #right-sidebar>
          <FeaturePanelSidebar />
        </template>
      </ContentLayout>
    </Container>
  </Main>
</template>
